import React, { useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, ExternalLink } from 'lucide-react';
import { cases, ui } from '../data/mock';
import Footer from '../components/Footer';

const CaseDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const refs = useRef([]);

  const idx = cases.findIndex((c) => c.slug === slug);
  const c = cases[idx];
  const next = cases[(idx + 1) % cases.length];

  // Build chapter list — prefer explicit 'chapter' markers; fall back to text section headings
  const chapters = useMemo(() => {
    if (!c) return [];
    const explicit = c.sections
      .map((s, i) => ({ ...s, sectionIndex: i }))
      .filter((s) => s.type === 'chapter' && s.heading);
    const source = explicit.length > 0
      ? explicit
      : c.sections
          .map((s, i) => ({ ...s, sectionIndex: i }))
          .filter((s) => s.type === 'text' && s.heading);
    return source.map((s, chIdx) => ({
      anchorId: `ch-${chIdx + 1}`,
      label: s.heading,
      sectionIndex: s.sectionIndex,
      number: chIdx + 1
    }));
  }, [c]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible'));
    }, { threshold: 0.15 });
    refs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [slug]);

  const scrollToAnchor = (anchorId) => (e) => {
    e.preventDefault();
    const el = document.getElementById(anchorId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', `#${anchorId}`);
    }
  };

  if (!c) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="font-display text-3xl text-[var(--cream)] mb-6">Case ikke fundet.</p>
          <button onClick={() => navigate('/work')} className="btn-ghost">Tilbage til arbejder</button>
        </div>
      </div>
    );
  }

  // Map each text section's position in c.sections to a chapter object (for ID rendering below)
  const chapterBySectionIndex = new Map(chapters.map((ch) => [ch.sectionIndex, ch]));

  return (
    <main className="min-h-screen w-full" style={{ background: 'var(--bg-dark)' }}>
      {/* Hero — text-only editorial, no cover image */}
      <section className="relative pt-40 pb-24 px-8 md:px-[90px] overflow-hidden">
        {/* Subtle atmospheric wash tinted by case accent */}
        <div className="absolute inset-0 pointer-events-none opacity-40" style={{
          background: `radial-gradient(circle at 85% 15%, ${c.accent}22 0%, transparent 55%), radial-gradient(circle at 10% 90%, ${c.accent}18 0%, transparent 60%)`
        }} />
        <div className="relative max-w-6xl">
          <Link to="/work" className="font-label text-[0.7rem] text-[var(--cream)]/60 hover:text-[var(--terracotta)] transition-colors duration-300 inline-flex items-center gap-2 mb-12">
            <ArrowLeft size={14} /> {ui.allWork}
          </Link>
          <div className="font-label text-[0.72rem] mb-8" style={{ color: c.accent }}>
            ————  {c.title}  /  {c.year}
          </div>
          <h1 className="font-display text-[clamp(3rem,9vw,8.5rem)] text-[var(--cream)] leading-[0.92]">
            {c.subtitle}
          </h1>
          {c.descriptor && (
            <p className="font-body text-[var(--cream)]/60 text-lg md:text-xl mt-10 max-w-2xl leading-relaxed">
              {c.descriptor}
            </p>
          )}
          {c.liveUrl && (
            <a
              href={c.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center gap-3 font-label text-[0.72rem] text-[var(--cream)] border border-[var(--cream)]/30 hover:border-[var(--terracotta)] hover:text-[var(--terracotta)] px-5 py-3 transition-colors duration-300"
            >
              <span>Se site live</span>
              <span className="text-[var(--cream)]/50">/</span>
              <span>{c.liveLabel || c.liveUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
              <ExternalLink size={13} />
            </a>
          )}
        </div>
      </section>

      {/* Metadata bar */}
      <section className="border-y border-[var(--cream)]/10 px-8 md:px-[90px] py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { k: ui.meta.client, v: c.client },
            { k: ui.meta.year, v: c.year },
            { k: ui.meta.role, v: c.role },
            { k: ui.meta.tools, v: c.tools }
          ].map((m) => (
            <div key={m.k}>
              <div className="font-label text-[0.62rem] text-[var(--cream)]/40 mb-2">{m.k}</div>
              <div className="font-heading text-[var(--cream)] text-base">{m.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Table of Contents — shown only if there are 2+ chapters */}
      {chapters.length >= 2 && (
        <section className="px-8 md:px-[90px] pt-24 pb-8 border-b border-[var(--cream)]/10">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-3">
              <div className="font-label text-[0.7rem] text-[var(--terracotta)] mb-3">Kapitler</div>
              <p className="font-body text-[var(--cream)]/50 text-sm leading-relaxed max-w-xs">
                Spring til et afsnit. {chapters.length} kapitler i alt.
              </p>
            </div>
            <ul className="md:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
              {chapters.map((ch) => (
                <li key={ch.anchorId}>
                  <a
                    href={`#${ch.anchorId}`}
                    onClick={scrollToAnchor(ch.anchorId)}
                    className="group flex items-baseline gap-5 py-3 border-t border-[var(--cream)]/10 hover:border-[var(--terracotta)] transition-colors duration-300"
                  >
                    <span className="font-label text-[0.66rem] text-[var(--cream)]/40 tabular-nums shrink-0 w-6" style={{ color: c.accent }}>
                      {String(ch.number).padStart(2, '0')}
                    </span>
                    <span className="font-heading text-[var(--cream)] text-lg md:text-xl leading-snug group-hover:text-[var(--terracotta)] transition-colors duration-300 flex-1">
                      {ch.label}
                    </span>
                    <ArrowRight size={14} className="text-[var(--cream)]/30 group-hover:text-[var(--terracotta)] transition-all duration-300 group-hover:translate-x-1 shrink-0" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Summary */}
      <section className="px-8 md:px-[90px] py-32">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <div className="font-label text-[0.7rem] text-[var(--terracotta)]">{ui.overview}</div>
          </div>
          <p
            ref={(el) => (refs.current[0] = el)}
            className="reveal md:col-span-9 font-body text-[clamp(1.4rem,2.4vw,2.1rem)] text-[var(--cream)]/85 leading-[1.5] max-w-4xl"
          >
            {c.summary}
          </p>
        </div>
      </section>

      {/* Sections — alternating image / text (text sections carry chapter anchors) */}
      {c.sections.map((s, i) => {
        const chapter = chapterBySectionIndex.get(i);
        // Detect ultra-wide images (aspect ratio > 3:1) — these need horizontal scroll on mobile
        const aspectParts = s.aspect ? s.aspect.split('/').map((x) => parseFloat(x.trim())) : null;
        const isUltraWide = aspectParts && aspectParts[0] / aspectParts[1] > 3;
        return (
          <section
            key={`${c.slug}-${s.type}-${s.src || s.heading || i}`}
            id={chapter ? chapter.anchorId : undefined}
            ref={(el) => (refs.current[i + 1] = el)}
            className="reveal px-8 md:px-[90px] mb-32 scroll-mt-28"
            style={{ transitionDelay: `${i * 80}ms` }}
          >
            {s.type === 'image' ? (
              <figure className="max-w-7xl mx-auto">
                {isUltraWide ? (
                  // Ultra-wide image — scroll horizontally on mobile, fit width on desktop
                  <div className="relative bg-[var(--bg-warm)] overflow-x-auto md:overflow-hidden">
                    <img
                      src={s.src}
                      alt={s.caption || c.subtitle}
                      className="block h-[180px] md:h-auto w-auto md:w-full max-w-none md:max-w-full object-contain"
                      style={{ aspectRatio: s.aspect }}
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="relative overflow-hidden bg-[var(--bg-warm)]" style={{ aspectRatio: s.aspect || '16 / 10' }}>
                    <img
                      src={s.src}
                      alt={s.caption || c.subtitle}
                      className={`absolute inset-0 w-full h-full ${s.fit === 'contain' ? 'object-contain' : 'object-cover'}`}
                      loading="lazy"
                    />
                  </div>
                )}
                <figcaption className="font-label text-[0.66rem] text-[var(--cream)]/40 mt-5">
                  {isUltraWide && (
                    <span className="md:hidden inline-flex items-center gap-2 mr-3 text-[var(--terracotta)]">
                      <ArrowRight size={11} /> Rul for at se hele billedet
                    </span>
                  )}
                  {s.caption}
                </figcaption>
              </figure>
            ) : s.type === 'video' ? (
              <figure className="max-w-7xl mx-auto">
                <div className="relative overflow-hidden bg-[var(--bg-warm)]" style={{ aspectRatio: s.aspect || '16 / 9' }}>
                  <video
                    src={s.src}
                    poster={s.poster}
                    autoPlay={s.autoplay !== false}
                    muted
                    loop={s.loop !== false}
                    playsInline
                    controls={s.controls === true}
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover"
                  >
                    <track kind="captions" />
                  </video>
                </div>
                <figcaption className="font-label text-[0.66rem] text-[var(--cream)]/40 mt-5">
                  {s.caption}
                </figcaption>
              </figure>
            ) : s.type === 'chapter' ? (
              <div className="max-w-7xl mx-auto pt-8">
                <div className="flex items-baseline gap-6 border-t border-[var(--cream)]/10 pt-10">
                  {chapter && (
                    <span className="font-label text-[0.66rem] tabular-nums shrink-0" style={{ color: c.accent }}>
                      Kap. {String(chapter.number).padStart(2, '0')}
                    </span>
                  )}
                  <h2 className="font-display text-3xl md:text-5xl text-[var(--cream)] leading-tight">
                    {s.heading}
                  </h2>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-12 gap-10 max-w-7xl mx-auto">
                <div className="md:col-span-4">
                  {chapter && (
                    <div className="font-label text-[0.66rem] mb-4" style={{ color: c.accent }}>
                      Kap. {String(chapter.number).padStart(2, '0')}
                    </div>
                  )}
                  <h3 className="font-display text-3xl md:text-4xl text-[var(--cream)]">
                    {s.heading}
                  </h3>
                </div>
                <div className="md:col-span-8">
                  <p className="font-body text-[var(--cream)]/65 text-lg leading-[1.7] max-w-2xl">
                    {s.body}
                  </p>
                </div>
              </div>
            )}
          </section>
        );
      })}

      {/* Next case */}
      <section className="px-8 md:px-[90px] pb-32 pt-16 border-t border-[var(--cream)]/10">
        <Link to={`/work/${next.slug}`} className="group block">
          <div className="font-label text-[0.7rem] text-[var(--cream)]/40 mb-6">{ui.nextCase}</div>
          <div className="flex items-baseline justify-between flex-wrap gap-6">
            <h2 className="font-display text-[clamp(2.5rem,7vw,7rem)] text-[var(--cream)] group-hover:text-[var(--terracotta)] transition-colors duration-500">
              {next.subtitle}
            </h2>
            <div className="flex items-center gap-4 font-label text-[0.7rem] text-[var(--cream)]/60 group-hover:text-[var(--terracotta)] transition-colors duration-500">
              <span>{ui.viewCase}</span>
              <span className="w-12 h-12 rounded-full border border-current flex items-center justify-center transition-transform duration-500 group-hover:rotate-45">
                <ArrowRight size={16} />
              </span>
            </div>
          </div>
          <p className="font-body text-[var(--cream)]/50 mt-5 max-w-xl">
            {next.descriptor}
          </p>
        </Link>
      </section>

      <Footer />
    </main>
  );
};

export default CaseDetail;
